<template>
  <div id="app">
    <Layout v-if="useLayoutPage && render && isLoginSuccess" />
    <router-view v-if="!useLayoutPage" />
    <IsLoading />
    <AlertComponent />
  </div>
</template>

<script lang="ts">
import AlertComponent from "@/components/element/AlertComponent.vue";
import IsLoading from "@/components/element/IsLoading.vue";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import {
  BroadcastTabChannelMasterTabClose,
  CheckBroadcastIsSupport,
  SetTabChannel,
} from "./broadcastChannel/tabChannel";
import { LoadingStatusType } from "./enums/enums";
import { SocketConnect, SocketDisconnect } from "./helper/notificationHelper";
import {
  ClearMasterTabFromCookies,
  ClearTabFromSessionStorage,
  GetTabIdFromSessionStorage,
  IsMasterTab,
  IsMasterTabAvailable,
  RemoveTabDB,
} from "./helper/tabHelper";
import { CheckIndexedDBIsSupport } from "./IndexedDB/tabIndexedDB";
import Layout from "./views/Layout.vue";

const StoreLogIn = namespace("LogIn");
const StoreAccount = namespace("Account");

@Component({
  components: {
    Layout,
    IsLoading,
    AlertComponent,
  },
})
export default class App extends Vue {
  private useLayoutPage = false;
  private render = true;

  @StoreAccount.Mutation
  private SetAccountListLoadingStatus!: (status: LoadingStatusType) => void;

  @StoreLogIn.Getter
  private getLoginLoadingStatus!: LoadingStatusType;

  created() {
    SetTabChannel();
  }

  mounted() {
    window.addEventListener("focus", () => {
      if (this.getLoginLoadingStatus == LoadingStatusType.Success && !IsMasterTabAvailable()) {
        SocketConnect();
      }
    });
    window.addEventListener("beforeunload", async () => {
      SocketDisconnect();
      if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
        const tabId = GetTabIdFromSessionStorage();
        const isMasterTab = IsMasterTab();
        if (isMasterTab) {
          ClearMasterTabFromCookies();
          BroadcastTabChannelMasterTabClose();
        }
        if (tabId) {
          ClearTabFromSessionStorage();
          await RemoveTabDB(tabId);
        }
      }
    });
  }

  destroyed() {
    localStorage.removeItem("lang");
  }
  get isLoginSuccess(): boolean {
    return this.getLoginLoadingStatus === LoadingStatusType.Success;
  }
  @Watch("$localStorage.lang", { immediate: true })
  localStorageLangChanged(newVal: any, oldVal: any) {
    if (localStorage.lang != null) {
      this.$i18n.locale = localStorage.lang;
    }
  }

  @Watch("$route", { immediate: true })
  routeChanged(newVal: any) {
    this.setWebTitle(this.$t(newVal.meta.title));
    if (newVal.name) {
      this.setUseLayoutPage(newVal.name);
    }
  }

  @Watch("$i18n.locale")
  localeChanged(newVal: any) {
    localStorage.setItem("lang", newVal);
    this.render = false;
    this.setWebTitle(this.$t(this.$route.meta?.title));
    this.SetAccountListLoadingStatus(LoadingStatusType.None);
    this.$nextTick(() => {
      this.render = true;
    });
  }

  public setUseLayoutPage(routerName: string) {
    switch (routerName) {
      case "login":
      case "register":
      case "welcome":
      case "selectList":
      case "logout":
      case "statement":
      case "pageNotFound":
      case "sessionExpire":
      case "canNotAccessMerchant":
      case "canNotAccess":
      case "update":
      case "acceptInvitation":
      case "forgotPwd":
      case "resetPwd":
        this.useLayoutPage = false;
        break;

      default:
        this.useLayoutPage = true;
        break;
    }
  }

  public setWebTitle(title: any) {
    if (title) {
      document.title = title;
    }
  }
}
</script>

<style lang="scss">
@import url("./assets/plugin/Bootstap/css/bootstrap.min.css");
@import url("./assets/iconFont/css/PEAK-Icon-Pack.css");
@import url("./assets/css/animate.min.css");

@font-face {
  font-family: "Maibork-lig";
  src: url("./assets/font/sukhumvittadmai/sukhumvittadmai_lig.woff") format("woff");
  font-display: auto;
}

@font-face {
  font-family: "Maibork-bold";
  src: url("./assets/font/sukhumvittadmai_bold/sukhumvittadmai_sebold.woff") format("woff");
  font-display: auto;
}

@font-face {
  font-family: "micrenc";
  src: url("./assets/font/micrenc/micrenc.woff") format("woff");
  font-display: auto;
}

.master {
  @include master;
  .box {
    @include box;
  }
}

@media print {
  #header,
  #footer,
  #breadcrumb,
  .headerSet {
    display: none;
  }
  p {
    font-size: 12px !important;
  }
  #statement #box {
    display: block !important;
    border: none !important;
    -webkit-print-color-adjust: exact;
    width: auto !important;
  }
  #layout {
    background: white;
  }
  #layout .contant {
    margin: 0 !important;
    padding: 0 !important;
    width: 272mm !important;
  }
  body {
    background-color: white;
  }
}
</style>
